import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customersApi, type Customer, type PaginatedResponse, type CreateCustomerInput } from '../lib/api';
import { fetchAllPages } from '../lib/pagination';

export function useCustomers(params?: {
  limit?: number;
  lastKey?: string;
  fetchAll?: boolean; // If true, fetches all pages (default: false to prevent excessive API calls)
}) {
  return useQuery<PaginatedResponse<Customer>>({
    queryKey: ['customers', params],
    queryFn: async () => {
      if (params?.fetchAll === true) {
        // Only fetch all pages if explicitly requested
        const allItems = await fetchAllPages<Customer>(
          (pageParams) => customersApi.list(pageParams),
          { limit: params?.limit || 500, maxPages: 20 }
        );
        return { items: allItems, lastKey: null };
      } else {
        // Fetch single page by default
        return customersApi.list(params);
      }
    },
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation<{ customer: string; customer_location?: string }, Error, CreateCustomerInput>({
    mutationFn: customersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, string>({
    mutationFn: customersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['packs'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation<{ customer: string; customer_location: string }, Error, { customer: string; data: { customer_location: string } }>({
    mutationFn: ({ customer, data }) => customersApi.update(customer, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['packs'] });
    },
  });
}

